import { useState, useRef, useCallback, useEffect } from "react";

// EN-4743. The capture countdown, timed exactly like the kiosk's
// (visitly-kiosk-app/src/components/IDCaptureModal.js, startCountdown): it shows 5,
// ticks once a second, and ends when it shows 1 - about 4 seconds.
export const CAPTURE_COUNTDOWN_SECONDS = 5;
// Kiosk MAX_GLARE_RETRIES: glare at the moment the countdown ends restarts it, at
// most this many times, so lighting that never improves doesn't hold the visitor
// here forever.
export const MAX_GLARE_RESTARTS = 2;

interface UseCaptureCountdownOptions {
  // Called once when the countdown ends (after any glare restarts).
  onElapsed: () => void;
  // Read at the moment the countdown ends.
  isGlare?: () => boolean;
}

interface UseCaptureCountdownReturn {
  secondsLeft: number;
  running: boolean;
  glareRestarts: number;
  // Starts a fresh countdown. Does nothing while one is running (kiosk
  // maybeStartCountdown), so a double call never hands out extra seconds.
  start: () => void;
  stop: () => void;
}

export const useCaptureCountdown = ({ onElapsed, isGlare }: UseCaptureCountdownOptions): UseCaptureCountdownReturn => {
  const [secondsLeft, setSecondsLeft] = useState<number>(CAPTURE_COUNTDOWN_SECONDS);
  const [running, setRunning] = useState<boolean>(false);
  const [glareRestarts, setGlareRestarts] = useState<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runningRef = useRef<boolean>(false);
  const glareRestartsRef = useRef<number>(0);
  // Latest callbacks, so a running interval never calls a stale closure.
  const onElapsedRef = useRef(onElapsed);
  const isGlareRef = useRef(isGlare);
  useEffect(() => {
    onElapsedRef.current = onElapsed;
    isGlareRef.current = isGlare;
  }, [onElapsed, isGlare]);

  const clear = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const stop = useCallback(() => {
    clear();
    runningRef.current = false;
    setRunning(false);
  }, [clear]);

  const start = useCallback(() => {
    if (runningRef.current) return;
    clear();
    runningRef.current = true;
    glareRestartsRef.current = 0;
    setGlareRestarts(0);
    setRunning(true);
    let timeLeft = CAPTURE_COUNTDOWN_SECONDS;
    setSecondsLeft(timeLeft);
    timerRef.current = setInterval(() => {
      timeLeft -= 1;
      setSecondsLeft(timeLeft);
      if (timeLeft > 1) return;

      // Glare at the photo moment: go round again from the top.
      if (isGlareRef.current?.() && glareRestartsRef.current < MAX_GLARE_RESTARTS) {
        glareRestartsRef.current += 1;
        setGlareRestarts(glareRestartsRef.current);
        timeLeft = CAPTURE_COUNTDOWN_SECONDS;
        setSecondsLeft(timeLeft);
        return;
      }
      clear();
      runningRef.current = false;
      setRunning(false);
      onElapsedRef.current();
    }, 1000);
  }, [clear]);

  useEffect(() => clear, [clear]);

  return { secondsLeft, running, glareRestarts, start, stop };
};
